import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectHeader from './components/ProjectHeader';
import ManuscriptEditor from './components/ManuscriptEditor';
import OutlineView from './components/OutlineView';
import CoverDesignTab from './components/CoverDesignTab';
import PublishingSettings from './components/PublishingSettings';
import VersionHistory from './components/VersionHistory';

const BookProjectDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('manuscript');
  const [project, setProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/user-login');
      return;
    }

    const mockProject = {
      id: location?.state?.projectId || 1,
      title: "The Entrepreneur\'s Guide to AI",
      genre: "Business & Technology",
      targetAudience: "Entrepreneurs, Business Owners, Tech Professionals",
      status: "in-progress",
      progress: 65,
      wordCount: 45230,
      chapterCount: 12,
      creditsUsed: 87,
      createdDate: "Nov 15, 2025",
      lastModified: "Dec 3, 2025",
      chapters: [
      {
        id: 1,
        title: "Chapter 1: Understanding AI in Business",
        content: `Artificial Intelligence has transformed from a futuristic concept into a practical business tool that entrepreneurs can leverage today. This chapter explores the fundamental concepts of AI and how they apply to modern business operations.\n\nThe journey of AI in business began decades ago, but recent advances in machine learning and natural language processing have made it accessible to businesses of all sizes. Today's entrepreneurs don't need to be technical experts to harness the power of AI.\n\nKey areas where AI is making an impact include customer service automation, predictive analytics, personalized marketing, and operational efficiency. Understanding these applications is crucial for any business leader looking to stay competitive in the digital age.\n\nThroughout this book, we'll explore practical strategies for implementing AI in your business, avoiding common pitfalls, and maximizing the return on your AI investments. Whether you're running a startup or managing an established company, AI offers opportunities to innovate and grow.`,
        wordCount: 1245,
        isComplete: true,
        lastEdited: "Dec 3, 2025"
      },
      {
        id: 2,
        title: "Chapter 2: AI Tools for Marketing",
        content: `Marketing has been revolutionized by artificial intelligence, offering entrepreneurs unprecedented capabilities to reach and engage their target audiences. This chapter examines the most effective AI-powered marketing tools and strategies.\n\nFrom content generation to customer segmentation, AI enables marketers to work smarter and achieve better results with less manual effort. Modern AI tools can analyze customer behavior patterns, predict purchasing decisions, and personalize marketing messages at scale.\n\nEmail marketing automation powered by AI can significantly improve open rates and conversions by sending the right message to the right person at the optimal time. Social media management tools use AI to schedule posts, analyze engagement, and even generate content ideas.\n\nThe key to successful AI marketing is understanding how to combine human creativity with machine efficiency. While AI can handle data analysis and repetitive tasks, the strategic vision and emotional connection still require human insight.`,
        wordCount: 1189,
        isComplete: true,
        lastEdited: "Dec 2, 2025"
      },
      {
        id: 3,
        title: "Chapter 3: Customer Service Automation",
        content: `Customer service represents one of the most impactful applications of AI in business. This chapter explores how chatbots, virtual assistants, and automated support systems can enhance customer satisfaction while reducing operational costs.\n\nModern AI chatbots can handle routine inquiries 24/7, providing instant responses to common questions and freeing human agents to focus on complex issues that require empathy and creative problem-solving.\n\nImplementing AI customer service requires careful planning and ongoing optimization. The best systems seamlessly blend automated responses with human intervention when needed, ensuring customers always receive appropriate support.`,
        wordCount: 987,
        isComplete: true,
        lastEdited: "Dec 1, 2025"
      },
      {
        id: 4,
        title: "Chapter 4: Data Analytics and Insights",
        content: `Data-driven decision making is essential for modern businesses, and AI makes it possible to extract meaningful insights from vast amounts of information. This chapter covers AI-powered analytics tools and techniques.\n\nPredictive analytics uses historical data to forecast future trends, helping entrepreneurs make informed decisions about inventory, staffing, and strategic planning. Machine learning algorithms can identify patterns that humans might miss, revealing opportunities for growth and efficiency improvements.`,
        wordCount: 856,
        isComplete: false,
        lastEdited: "Nov 30, 2025"
      }],

      outline: [
      {
        id: 1,
        number: 1,
        title: "Understanding AI in Business",
        description: "Introduction to artificial intelligence concepts and their practical applications in modern business environments.",
        wordCount: 1245,
        isComplete: true,
        subsections: [
        { title: "What is AI?", description: "Fundamental concepts and definitions" },
        { title: "AI vs Traditional Software", description: "Key differences and advantages" },
        { title: "Business Applications", description: "Real-world use cases" }],

        keyPoints: [
        "AI has evolved from theory to practical business tool",
        "No technical expertise required to leverage AI",
        "Focus on customer service, analytics, and automation"]

      },
      {
        id: 2,
        number: 2,
        title: "AI Tools for Marketing",
        description: "Comprehensive guide to AI-powered marketing tools and strategies for entrepreneurs.",
        wordCount: 1189,
        isComplete: true,
        subsections: [
        { title: "Content Generation", description: "AI-assisted content creation" },
        { title: "Customer Segmentation", description: "Automated audience analysis" },
        { title: "Campaign Optimization", description: "AI-driven performance improvement" }],

        keyPoints: [
        "Combine human creativity with AI efficiency",
        "Personalization at scale through automation",
        "Data-driven marketing decisions"]

      },
      {
        id: 3,
        number: 3,
        title: "Customer Service Automation",
        description: "Implementing AI chatbots and virtual assistants to enhance customer support.",
        wordCount: 987,
        isComplete: true,
        subsections: [
        { title: "Chatbot Implementation", description: "Setting up automated support" },
        { title: "Human-AI Collaboration", description: "Blending automation with personal touch" }],

        keyPoints: [
        "24/7 availability improves customer satisfaction",
        "Reduce operational costs while maintaining quality",
        "Seamless handoff between AI and human agents"]

      }],

      coverDesigns: [
      {
        id: 1,
        name: "Modern Professional",
        style: "Minimalist Corporate",
        frontCover: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb723524-1764663118982.png",
        frontCoverAlt: "Modern minimalist book cover with geometric blue and white design featuring bold typography and abstract AI circuit patterns on professional business background",
        backCover: "https://images.unsplash.com/photo-1637087788275-9b328d83ad36",
        backCoverAlt: "Back cover with clean white background featuring book description text, author bio section, and barcode placement in professional business style",
        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb723524-1764663118982.png",
        thumbnailAlt: "Thumbnail preview of modern professional book cover design with blue geometric patterns",
        colors: ["#1E40AF", "#FFFFFF", "#F3F4F6"],
        typography: "Inter Bold + Source Sans Pro",
        confidence: 92,
        competitorInsights: [
        "Bold typography performs 34% better in business category",
        "Blue color scheme associated with trust and professionalism",
        "Minimalist designs have higher conversion rates"]

      },
      {
        id: 2,
        name: "Tech Innovation",
        style: "Futuristic Digital",
        frontCover: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb43077f-1764663757339.png",
        frontCoverAlt: "Futuristic book cover with vibrant purple and blue gradient background featuring digital network patterns, AI neural connections, and modern sans-serif typography",
        backCover: "https://images.unsplash.com/photo-1644060473538-dbbe07fd5195",
        backCoverAlt: "Back cover with gradient purple background, structured text layout for book description, and modern design elements",
        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10ae3d329-1764669835333.png",
        thumbnailAlt: "Thumbnail of tech innovation cover with purple gradient and digital patterns",
        colors: ["#7C3AED", "#3B82F6", "#1E293B"],
        typography: "JetBrains Mono + Inter",
        confidence: 88,
        competitorInsights: [
        "Purple conveys innovation and creativity",
        "Digital patterns attract tech-savvy audience",
        "Gradient backgrounds trending in tech books"]

      },
      {
        id: 3,
        name: "Executive Authority",
        style: "Premium Classic",
        frontCover: "https://img.rocket.new/generatedImages/rocket_gen_img_142405494-1764664277433.png",
        frontCoverAlt: "Premium classic book cover with deep navy blue background, gold metallic accents, elegant serif typography, and subtle texture suggesting leather binding",
        backCover: "https://img.rocket.new/generatedImages/rocket_gen_img_117cc9ef1-1764668331007.png",
        backCoverAlt: "Back cover with navy background, gold border details, professional text layout, and premium finish appearance",
        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb8d8bfb-1764875432970.png",
        thumbnailAlt: "Thumbnail of executive authority cover with navy and gold premium design",
        colors: ["#0F172A", "#F59E0B", "#E5E7EB"],
        typography: "Playfair Display + Inter",
        confidence: 85,
        competitorInsights: [
        "Gold accents signal premium positioning",
        "Classic design appeals to executive audience",
        "Navy blue associated with authority and expertise"]

      }],

      publishingSettings: {
        title: "The Entrepreneur\'s Guide to AI",
        subtitle: "Practical Strategies for Business Growth",
        author: "John Smith",
        isbn: "",
        publicationDate: "2026-01-15",
        description: "A comprehensive guide for entrepreneurs looking to leverage artificial intelligence in their business operations. Learn practical strategies, avoid common pitfalls, and discover how AI can transform your business.",
        primaryCategory: "business",
        secondaryCategory: "marketing",
        keywords: ["artificial intelligence", "business automation", "entrepreneurship", "digital transformation", "machine learning"],
        trimSize: "6x9",
        interiorType: "Black & White",
        paperType: "White"
      },
      versionHistory: [
      {
        id: 1,
        name: "Current Draft",
        type: "manual-save",
        date: "Dec 3, 2025",
        time: "2:45 PM",
        wordCount: 45230,
        description: "Latest manual save with chapter 4 updates",
        isCurrent: true,
        changes: [
        { type: "modified", description: "Updated Chapter 4 content" },
        { type: "added", description: "Added new subsection on predictive analytics" }]

      },
      {
        id: 2,
        name: "Chapter 3 Complete",
        type: "milestone",
        date: "Dec 1, 2025",
        time: "4:30 PM",
        wordCount: 43890,
        description: "Completed customer service automation chapter",
        isCurrent: false,
        changes: [
        { type: "added", description: "Completed Chapter 3" },
        { type: "modified", description: "Revised introduction" }]

      },
      {
        id: 3,
        name: "AI Generation - Marketing Chapter",
        type: "ai-generation",
        date: "Nov 30, 2025",
        time: "10:15 AM",
        wordCount: 42450,
        description: "AI-generated content for marketing tools chapter",
        isCurrent: false,
        changes: [
        { type: "added", description: "Generated Chapter 2 content" },
        { type: "added", description: "Added marketing examples" }]

      }]

    };

    setProject(mockProject);
  }, [navigate, location]);

  const handleSaveProject = async (updatedProject) => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setProject(updatedProject);
    setIsSaving(false);
  };

  const handleExport = () => {
    console.log('Exporting project...');
  };

  const handlePublish = () => {
    navigate('/ai-agent-workflow-hub', { state: { action: 'publish', projectId: project?.id } });
  };

  const handleChapterUpdate = (chapterIndex, content) => {
    const updatedChapters = [...project?.chapters];
    updatedChapters[chapterIndex] = {
      ...updatedChapters?.[chapterIndex],
      content,
      lastEdited: new Date()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    };
    setProject({ ...project, chapters: updatedChapters });
  };

  const handleRegenerateChapter = async (chapterIndex) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Regenerating chapter:', chapterIndex);
  };

  const handleOutlineReorder = (fromIndex, toIndex) => {
    const updatedOutline = [...project?.outline];
    const [movedItem] = updatedOutline?.splice(fromIndex, 1);
    updatedOutline?.splice(toIndex, 0, movedItem);
    setProject({ ...project, outline: updatedOutline });
  };

  const handleOutlineUpdate = (index, title) => {
    const updatedOutline = [...project?.outline];
    updatedOutline[index] = { ...updatedOutline?.[index], title };
    setProject({ ...project, outline: updatedOutline });
  };

  const handleSelectDesign = (designId) => {
    console.log('Selected design:', designId);
  };

  const handleCustomizeDesign = (design) => {
    console.log('Customizing design:', design);
  };

  const handleRegenerateDesign = async () => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Regenerating cover designs...');
  };

  const handleUpdatePublishingSettings = (settings) => {
    setProject({ ...project, publishingSettings: settings });
  };

  const handlePreviewBook = () => {
    console.log('Previewing book...');
  };

  const handleRestoreVersion = (versionId) => {
    console.log('Restoring version:', versionId);
  };

  const handleCompareVersions = (version1Id, version2Id) => {
    console.log('Comparing versions:', version1Id, version2Id);
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading project...</p>
        </div>
      </div>);

  }

  const tabs = [
  { id: 'manuscript', label: 'Manuscript', icon: 'FileText' },
  { id: 'outline', label: 'Outline', icon: 'List' },
  { id: 'cover', label: 'Cover Design', icon: 'Image' },
  { id: 'publishing', label: 'Publishing', icon: 'Send' },
  { id: 'versions', label: 'Versions', icon: 'History' }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/user-dashboard')}>

              Back to Dashboard
            </Button>
          </div>

          <div className="space-y-6">
            <ProjectHeader
              project={project}
              onSave={handleSaveProject}
              onExport={handleExport}
              onPublish={handlePublish} />


            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
              <div className="border-b border-border overflow-x-auto">
                <div className="flex">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors duration-200 border-b-2 ${
                    activeTab === tab?.id ?
                    'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'}`
                    }>

                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'manuscript' &&
                <ManuscriptEditor
                  chapters={project?.chapters}
                  onChapterUpdate={handleChapterUpdate}
                  onRegenerateChapter={handleRegenerateChapter} />

                }

                {activeTab === 'outline' &&
                <OutlineView
                  outline={project?.outline}
                  onReorder={handleOutlineReorder}
                  onUpdateSection={handleOutlineUpdate} />

                }

                {activeTab === 'cover' &&
                <CoverDesignTab
                  designs={project?.coverDesigns}
                  onSelectDesign={handleSelectDesign}
                  onCustomize={handleCustomizeDesign}
                  onRegenerate={handleRegenerateDesign} />

                }

                {activeTab === 'publishing' &&
                <PublishingSettings
                  settings={project?.publishingSettings}
                  onUpdate={handleUpdatePublishingSettings}
                  onPreview={handlePreviewBook} />

                }

                {activeTab === 'versions' &&
                <VersionHistory
                  versions={project?.versionHistory}
                  onRestore={handleRestoreVersion}
                  onCompare={handleCompareVersions} />

                }
              </div>
            </div>
          </div>
        </div>
      </main>
      {isSaving &&
      <div className="fixed bottom-4 right-4 bg-card border border-border rounded-lg shadow-lg p-4 flex items-center gap-3 animate-fade-in">
          <Icon name="Loader" size={20} className="text-primary animate-spin" />
          <span className="text-sm text-foreground">Saving changes...</span>
        </div>
      }
    </div>);

};

export default BookProjectDetails;